import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import bankAccountService, { BankAccountFormData } from '../../services/bankAccountService';
import { chartOfAccountService } from '../../services/chartOfAccountService';
import { BankAccount } from '../../types';
import { toast } from 'react-hot-toast';

interface BankAccountFormProps {
  account?: BankAccount | null;
  onClose: () => void;
  onSuccess: () => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
  outline: 'none', colorScheme: 'light',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#6b6a5c', marginBottom: 6,
};

const BankAccountForm: React.FC<BankAccountFormProps> = ({ account, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<BankAccountFormData>({
    accountName: '', accountNumber: '', bankName: '', accountType: 'checking', balance: 0, chartOfAccountId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: assetAccountsResponse, isLoading: assetAccountsLoading } = useQuery({
    queryKey: ['chart-of-accounts', 'ASSET'],
    queryFn: () => chartOfAccountService.getChartOfAccounts({ accountType: 'ASSET', isActive: true, limit: 500 }),
  });

  const glAccountOptions = (assetAccountsResponse?.data || []).filter(
    acc => acc.accountSubType === 'CASH' || acc.accountSubType === 'BANK'
  );

  useEffect(() => {
    if (account) {
      setFormData({
        accountName: account.accountName, accountNumber: account.accountNumber,
        bankName: account.bankName, accountType: account.accountType, balance: account.balance,
        chartOfAccountId: account.chartOfAccountId || '',
      });
    }
  }, [account]);

  const createMutation = useMutation({
    mutationFn: bankAccountService.createBankAccount,
    onSuccess: () => { toast.success('Bank account created successfully'); onSuccess(); },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create bank account');
      if (error?.response?.data?.errors) {
        const fieldErrors: Record<string, string> = {};
        error.response.data.errors.forEach((err: any) => { if (err.field) fieldErrors[err.field] = err.message; });
        setErrors(fieldErrors);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<BankAccountFormData>) => bankAccountService.updateBankAccount(account!.id, data),
    onSuccess: () => { toast.success('Bank account updated successfully'); onSuccess(); },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update bank account');
      if (error?.response?.data?.errors) {
        const fieldErrors: Record<string, string> = {};
        error.response.data.errors.forEach((err: any) => { if (err.field) fieldErrors[err.field] = err.message; });
        setErrors(fieldErrors);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const newErrors: Record<string, string> = {};
    if (!formData.accountName.trim()) newErrors.accountName = 'Account name is required';
    if (!formData.accountNumber.trim()) newErrors.accountNumber = 'Account number is required';
    if (!formData.bankName.trim()) newErrors.bankName = 'Bank name is required';
    if (!formData.accountType.trim()) newErrors.accountType = 'Account type is required';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    if (account) updateMutation.mutate(formData);
    else createMutation.mutate(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'balance' ? parseFloat(value) || 0 : value }));
    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const errBorder = (field: string) => errors[field] ? '1px solid rgba(201,90,90,0.4)' : '1px solid rgba(92,78,51,0.25)';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 50 }}>
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, padding: '28px 32px', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033', margin: 0 }}>
            {account ? 'Edit Bank Account' : 'Add New Bank Account'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, color: '#6b6a5c', cursor: 'pointer', padding: '4px 8px', fontSize: 16, lineHeight: 1 }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label htmlFor="accountName" style={labelStyle}>Account Name *</label>
            <input type="text" id="accountName" name="accountName" value={formData.accountName} onChange={handleChange} placeholder="e.g., Main Business Account" style={{ ...inputStyle, border: errBorder('accountName') }} />
            {errors.accountName && <p style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{errors.accountName}</p>}
          </div>

          <div>
            <label htmlFor="accountNumber" style={labelStyle}>Account Number *</label>
            <input type="text" id="accountNumber" name="accountNumber" value={formData.accountNumber} onChange={handleChange} placeholder="e.g., 1234567890" style={{ ...inputStyle, border: errBorder('accountNumber') }} />
            {errors.accountNumber && <p style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{errors.accountNumber}</p>}
          </div>

          <div>
            <label htmlFor="bankName" style={labelStyle}>Bank Name *</label>
            <input type="text" id="bankName" name="bankName" value={formData.bankName} onChange={handleChange} placeholder="e.g., National Bank of Bahrain" style={{ ...inputStyle, border: errBorder('bankName') }} />
            {errors.bankName && <p style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{errors.bankName}</p>}
          </div>

          <div>
            <label htmlFor="accountType" style={labelStyle}>Account Type *</label>
            <select id="accountType" name="accountType" value={formData.accountType} onChange={handleChange} style={{ ...inputStyle, border: errBorder('accountType') }}>
              <option value="checking">Checking Account</option>
              <option value="savings">Savings Account</option>
              <option value="business">Business Account</option>
              <option value="cash">Cash Account</option>
              <option value="credit">Credit Account</option>
            </select>
            {errors.accountType && <p style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{errors.accountType}</p>}
          </div>

          <div>
            <label htmlFor="chartOfAccountId" style={labelStyle}>Linked Chart of Account</label>
            <select
              id="chartOfAccountId"
              name="chartOfAccountId"
              value={formData.chartOfAccountId || ''}
              onChange={handleChange}
              style={inputStyle}
              disabled={assetAccountsLoading}
            >
              <option value="">Not linked</option>
              {glAccountOptions.map(acc => (
                <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
              ))}
            </select>
            <p style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
              Links this bank account to its GL account, so it can be selected as "Paid From" when recording a Direct Expense.
            </p>
          </div>

          <div>
            <label htmlFor="balance" style={labelStyle}>Opening Balance (BD)</label>
            <input type="number" id="balance" name="balance" value={formData.balance} onChange={handleChange} step="0.001" min="0" placeholder="0.000" style={inputStyle} />
            <p style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>Enter the current balance in this account</p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 16, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
            <button type="button" onClick={onClose} disabled={isLoading} style={{ padding: '10px 24px', background: 'none', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: isLoading ? 'not-allowed' : 'pointer' }}>
              Cancel
            </button>
            <button type="submit" disabled={isLoading} style={{ padding: '10px 24px', background: isLoading ? 'rgba(92,78,51,0.25)' : 'linear-gradient(135deg, #d97706, #b45309)', border: 'none', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: isLoading ? '#c9c2ab' : '#1c2033', cursor: isLoading ? 'not-allowed' : 'pointer' }}>
              {isLoading ? 'Saving...' : account ? 'Update Account' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankAccountForm;
